// Bajar una imagen y dejarla lista para embeber en un PDF —
// ADR-2026-09-08-el-pdf-con-estilos-e-imagenes.
//
// TODO pasa por canvas y sale como JPEG, sea lo que sea que entró. Dos razones:
//
//  1. El PDF embebe JPEG casi sin trabajo (`DCTDecode` toma los bytes tal cual). Un PNG
//     habría que re-comprimirlo y manejar paleta y transparencia a mano: mucho código para
//     un formato que en una foto de producto no aporta nada.
//  2. Pasar por canvas normaliza cualquier cosa que el navegador sepa decodificar (PNG,
//     WebP, AVIF) y de paso ACHICA. Una foto de catálogo de 4000px en un PDF son 3 MB por
//     imagen y el archivo se vuelve inmanejable.
//
// La transparencia se pierde: el JPEG no la tiene. Se rellena en blanco, que es lo que
// corresponde sobre una hoja.

import type { ImagenPdf } from './escribirPdf';

/** Lado máximo. A 1400px una foto ocupa una página entera sin verse pixelada. */
const LADO_MAX = 1400;
const CALIDAD = 0.82;

const cargar = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolver, rechazar) => {
    const img = new Image();
    // Sin esto el canvas queda "tainted" y `toDataURL` tira: las fotos vienen de Supabase,
    // que es otro origen.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolver(img);
    img.onerror = () => rechazar(new Error('No pude cargar la imagen.'));
    img.src = url;
  });

const bytesDeDataUrl = (dataUrl: string): Uint8Array => {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const bin = atob(base64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

/**
 * URL → imagen lista para el PDF. `null` si no se pudo (URL caída, firma vencida, un
 * origen que no manda CORS): el documento se arma igual sin esa foto, que es mejor que no
 * entregar nada.
 */
export async function imagenParaPdf(url: string): Promise<ImagenPdf | null> {
  try {
    const img = await cargar(url);
    const escala = Math.min(1, LADO_MAX / Math.max(img.naturalWidth, img.naturalHeight));
    const ancho = Math.max(1, Math.round(img.naturalWidth * escala));
    const alto = Math.max(1, Math.round(img.naturalHeight * escala));
    const canvas = document.createElement('canvas');
    canvas.width = ancho;
    canvas.height = alto;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // Fondo blanco: sin esto, lo transparente de un PNG sale NEGRO al pasar a JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, ancho, alto);
    ctx.drawImage(img, 0, 0, ancho, alto);
    const jpeg = bytesDeDataUrl(canvas.toDataURL('image/jpeg', CALIDAD));
    return jpeg.length > 0 ? { jpeg, ancho, alto } : null;
  } catch {
    return null;
  }
}
